import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export function RefIdChip({ requestId, testIdPrefix }) {
  const [copyState, setCopyState] = useState('idle');
  const refIdRef = useRef(null);

  const selectRefIdText = () => {
    const node = refIdRef.current;
    if (!node || typeof window === 'undefined' || !window.getSelection) return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const handleCopy = useCallback(async () => {
    if (!requestId) return;
    if (!navigator?.clipboard?.writeText) {
      selectRefIdText();
      setCopyState('manual');
      setTimeout(() => setCopyState('idle'), 2500);
      return;
    }
    try {
      await navigator.clipboard.writeText(requestId);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 1500);
    } catch (_) {
      selectRefIdText();
      setCopyState('manual');
      setTimeout(() => setCopyState('idle'), 2500);
    }
  }, [requestId]);

  if (!requestId) {
    return (
      <Typography
        data-testid={`${testIdPrefix}-no-reference`}
        style={{ fontSize: '13px', color: '#888' }}
      >
        No reference id was returned for this request.
      </Typography>
    );
  }

  const tooltipText = {
    idle: 'Copy reference id',
    copied: 'Copied',
    manual: 'Copy unavailable — text selected, press Ctrl/Cmd+C',
  }[copyState];

  return (
    <Box
      data-testid={`${testIdPrefix}-reference`}
      display="flex"
      alignItems="center"
      padding="6px 12px"
      style={{
        background: '#F5F5F5',
        border: '1px solid #E0E0E0',
        borderRadius: '4px',
        fontFamily: 'monospace',
        fontSize: '13px',
      }}
    >
      <Typography style={{ marginRight: '8px', fontFamily: 'inherit' }}>
        Ref:{' '}
        <span
          ref={refIdRef}
          data-testid={`${testIdPrefix}-request-id`}
          style={{ userSelect: 'all' }}
        >
          {requestId}
        </span>
      </Typography>
      <Tooltip title={tooltipText}>
        <IconButton
          size="small"
          aria-label="Copy reference id"
          onClick={handleCopy}
          data-testid={`${testIdPrefix}-copy`}
          data-copy-state={copyState}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

RefIdChip.propTypes = {
  requestId: PropTypes.string,
  testIdPrefix: PropTypes.string,
};

RefIdChip.defaultProps = {
  requestId: null,
  testIdPrefix: 'ref-id',
};

export default RefIdChip;
